import { useRecoilState } from "recoil";
import { authState } from "../../State/Auth";
import { useApi } from "../useApi";
import { Result, isErr, ok } from "../../Utils/Result";
import { useMemo, useEffect } from "react";
import { clearAllStates } from "../../State/Utils";
import {
  clearSupabaseAuthToken,
  getSupabaseAuthToken,
  supabase,
} from "./Supabase";
import { eventBus } from "../../EventBus";
import { Notification } from "../../../Components/Notifications";
import { registerState } from "../../State/Register";
import {
  ClientUserSsoProviderType,
} from "../../Types-DTOs/ClientUser";
import { z } from "zod";
import { invitationState } from "../../State/Invitation";
import { Invitation } from "../../Types-DTOs/Invitation";
import { ApiError } from "../useApi/Schemas";
import { LoginIntent } from "../../Types-DTOs/LoginIntent";
import { readAcquisition } from "../../Utils/acquisition";

const isRegisterPath = (pathname: string) => pathname.replace(/\/$/, "") === "/register";

export const useAuth = () => {
  const [auth, setAuth] = useRecoilState(authState);
  const [register, setRegister] = useRecoilState(registerState);
  const [_, setInvitation] = useRecoilState(invitationState);
  const {
    registerUserEndpoint,
    loginWithPasswordEndpoint,
    loginWithSsoEndpoint,
    registerUserSsoEndpoint,
    useInvitationEndpoint,
    useInvitationWithSsoEndpoint,
    getInvitationEndpoint,
    tryAgentLoginEndpoint,
    agentLoginIntentEndpoint,
    requestPasswordResetEndpoint,
    resetPasswordEndpoint,
  } = useApi();

  const registerUser = async (props: {
    businessName: string;
    clientEmail: string;
    clientPhone: string;
    clientName: string;
    clientPassword: string;
    turnstileToken: string;
  }) => {
    const response = await registerUserEndpoint({
      ...props,
      acquisition: readAcquisition(document.cookie, register.gclid),
      referralCode: register.referralCode,
    });

    if (isErr(response)) {
      return response;
    }
    const { clientUser, token } = response.value;

    setAuth({
      clientUser,
      token,
    });

    return response;
  };

  const registerUserWithSso = async (props: {
    businessName: string;
    clientPhone: string;
    clientName: string;
    clientPassword: string;
    clientSsoProvider: ClientUserSsoProviderType;
    clientAuthToken: string;
    turnstileToken: string;
  }) => {
    const response = await registerUserSsoEndpoint({
      ...props,
      acquisition: readAcquisition(document.cookie, register.gclid),
      referralCode: register.referralCode,
    });

    if (isErr(response)) {
      return response;
    }
    const { clientUser, token } = response.value;

    setAuth({
      clientUser,
      token,
    });

    return response;
  };

  const registerRedirectQuery = (provider: ClientUserSsoProviderType) => {
    const params = new URLSearchParams({ callback_provider: provider });
    if (register.gclid) {
      params.set("gclid", register.gclid);
    }
    if (register.referralCode) {
      params.set("ref", register.referralCode);
    }
    return params.toString();
  };

  const preRegisterUserWithGoogle = async () => {
    clearSupabaseAuthToken();
    supabase.auth.signInWithOAuth({
      provider: "google",
      options: {
        queryParams: {
          access_type: "offline",
          prompt: "consent",
        },
        redirectTo: `${location.protocol}//${location.host}/register/?${registerRedirectQuery(
          ClientUserSsoProviderType.GOOGLE
        )}`,
      },
    });
  };

  const preRegisterUserWithLinkedin = async () => {
    clearSupabaseAuthToken();
    supabase.auth.signInWithOAuth({
      provider: "linkedin_oidc",
      options: {
        queryParams: {
          access_type: "offline",
          prompt: "consent",
        },
        redirectTo: `${location.protocol}//${location.host}/register/?${registerRedirectQuery(
          ClientUserSsoProviderType.LINKEDIN
        )}`,
      },
    });
  };

  const preRegisterUserWithLinkedinUsingInvitation = async (
    invitationId: string
  ) => {
    clearSupabaseAuthToken();
    supabase.auth.signInWithOAuth({
      provider: "linkedin_oidc",
      options: {
        queryParams: {
          access_type: "offline",
          prompt: "consent",
        },
        redirectTo: `${location.protocol}//${location.host}/invitation/?callback_provider=${ClientUserSsoProviderType.LINKEDIN}&invitationId=${invitationId}`,
      },
    });
  };

  const preRegisterUserWithGoogleUsingInvitation = async (
    invitationId: string
  ) => {
    clearSupabaseAuthToken();
    supabase.auth.signInWithOAuth({
      provider: "google",
      options: {
        queryParams: {
          access_type: "offline",
          prompt: "consent",
        },
        redirectTo: `${location.protocol}//${location.host}/invitation/?callback_provider=${ClientUserSsoProviderType.GOOGLE}&invitationId=${invitationId}`,
      },
    });
  };

  const loginWithPassword = async (email: string, password: string) => {
    const response = await loginWithPasswordEndpoint({ email, password });

    if (isErr(response)) {
      return response;
    }

    const { clientUser, token } = response.value;

    setAuth({
      clientUser,
      token,
    });

    return response;
  };

  const loginWithGoogle = async () => {
    supabase.auth.signInWithOAuth({
      provider: "google",
      options: {
        queryParams: {
          access_type: "offline",
          prompt: "consent",
        },
        redirectTo: `${location.protocol}//${location.host}/login/?callback_provider=${ClientUserSsoProviderType.GOOGLE}`,
      },
    });
  };

  const loginWithLinkedin = async () => {
    supabase.auth.signInWithOAuth({
      provider: "linkedin_oidc",
      options: {
        redirectTo: `${location.protocol}//${location.host}/login?callback_provider=${ClientUserSsoProviderType.LINKEDIN}`,
      },
    });
  };

  const redirectPostLoginSso = () => {
    const queryParams = new URLSearchParams(window.location.search);

    const providerQuery = queryParams.get("callback_provider");

    if (providerQuery === null) {
      return;
    }

    getSupabaseAuthToken().then(async (supabaseToken) => {
      if (!supabaseToken) {
        eventBus.emit(
          "NOTIFICATION",
          Notification.buildNotification("error", "Error logging in")
        );
        return;
      }
      const result = await loginWithSsoEndpoint(supabaseToken);

      if (isErr(result)) {
        eventBus.emit(
          "NOTIFICATION",
          Notification.buildNotification(
            "error",
            "Usuario no encontrado, por favor regístrese antes de iniciar sesión."
          )
        );
        return;
      }

      const { clientUser, token } = result.value;

      setAuth({
        clientUser,
        token,
      });
    });
  };

  useEffect(() => {
    if (!isRegisterPath(window.location.pathname)) {
      return;
    }

    if (register.gclid) {
      return;
    }

    const gclid = new URLSearchParams(window.location.search).get("gclid");

    if (!gclid) {
      return;
    }

    setRegister((prev) => ({ ...prev, gclid }));
  }, [window.location]);

  useEffect(() => {
    if (!isRegisterPath(window.location.pathname)) {
      return;
    }

    if (register.referralCode) {
      return;
    }

    const referralCode = new URLSearchParams(window.location.search).get("ref");

    if (!referralCode) {
      return;
    }

    setRegister((prev) => ({ ...prev, referralCode }));
  }, [window.location]);

  useEffect(() => {
    if (window.location.pathname !== "/register/") {
      return;
    }

    if (register.ssoToken) {
      return;
    }

    const queryParams = new URLSearchParams(window.location.search);

    const providerQuery = queryParams.get("callback_provider");

    if (providerQuery === null) {
      return;
    }

    const providerParsed = z
      .nativeEnum(ClientUserSsoProviderType)
      .safeParse(providerQuery);

    if (!providerParsed.success) {
      return;
    }

    const provider = providerParsed.data;

    getSupabaseAuthToken().then(async (supabaseToken) => {
      if (!supabaseToken) {
        eventBus.emit(
          "NOTIFICATION",
          Notification.buildNotification("error", "Error logging in")
        );
        return;
      }

      setRegister({
        ...register,
        ssoToken: supabaseToken,
        ssoProvider: provider,
      });
    });
  }, [window.location]);

  useEffect(() => {
    if (window.location.pathname !== "/invitation/") {
      return;
    }

    if (register.ssoToken) {
      return;
    }

    const queryParams = new URLSearchParams(window.location.search);

    const providerQuery = queryParams.get("callback_provider");

    if (providerQuery === null) {
      return;
    }

    const providerParsed = z
      .nativeEnum(ClientUserSsoProviderType)
      .safeParse(providerQuery);

    if (!providerParsed.success) {
      return;
    }

    const provider = providerParsed.data;

    getSupabaseAuthToken().then(async (supabaseToken) => {
      if (!supabaseToken) {
        eventBus.emit(
          "NOTIFICATION",
          Notification.buildNotification("error", "Error logging in")
        );
        return;
      }

      setInvitation({
        ssoToken: supabaseToken,
        ssoProvider: provider,
      });
    });
  }, [window.location]);

  const useInvitation = async (props: {
    invitationId: string;
    email: string;
    password: string;
  }) => {
    const response = await useInvitationEndpoint(props);

    if (isErr(response)) {
      return response;
    }
    const { clientUser, token } = response.value;

    setAuth({
      clientUser,
      token,
    });

    return response;
  };

  const useInvitationWithSso = async (props: {
    invitationId: string;
    tokenSso: string;
    ssoProvider: ClientUserSsoProviderType;
  }) => {
    const response = await useInvitationWithSsoEndpoint(props);

    if (isErr(response)) {
      return response;
    }
    const { clientUser, token } = response.value;

    setAuth({
      clientUser,
      token,
    });

    return response;
  };

  const getInvitation = async (
    invitationId: string
  ): Promise<Result<Invitation, ApiError>> => {
    const response = await getInvitationEndpoint(invitationId);

    if (isErr(response)) {
      return response;
    }
    const invitation = response.value.invitation;

    return ok(invitation);
  };

  const tryAgentLogin = async (
    phone: string
  ): Promise<Result<LoginIntent, ApiError>> => {
    const response = await tryAgentLoginEndpoint(phone);

    if (isErr(response)) {
      return response;
    }

    const { loginIntent } = response.value;

    return ok(loginIntent);
  };

  const agentLogin = async (loginIntentId: string, code: string) => {
    const response = await agentLoginIntentEndpoint(loginIntentId, code);

    if (isErr(response)) {
      return response;
    }

    const { clientUser, token } = response.value;

    setAuth({
      clientUser,
      token,
    });

    return response;
  };

  const requestPasswordReset = async (email: string) => {
    const response = await requestPasswordResetEndpoint(email);

    if (isErr(response)) {
      return response;
    }

    return response;
  };

  const resetPassword = async (token: string, email: string, newPassword: string) => {
    const response = await resetPasswordEndpoint(token, email, newPassword);

    if (isErr(response)) {
      return response;
    }

    return response;
  };

  const isLoggedIn = useMemo(() => {
    return auth.token !== undefined;
  }, [auth.clientUser]);

  const logout = () => {
    clearAllStates();
  };

  return {
    preRegisterUserWithGoogle,
    preRegisterUserWithLinkedin,
    registerUser,
    registerUserWithSso,
    loginWithPassword,
    loginWithGoogle,
    loginWithLinkedin,
    redirectPostLoginSso,
    logout,
    preRegisterUserWithGoogleUsingInvitation,
    preRegisterUserWithLinkedinUsingInvitation,
    useInvitation,
    useInvitationWithSso,
    getInvitation,
    tryAgentLogin,
    agentLogin,
    requestPasswordReset,
    resetPassword,
    clientUser: auth.clientUser,
    isLoggedIn,
  };
};
